import { Person as PersonIcon } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import AddToCalendarButton from "components/AddToCalendarButton";
import CompanyLogoSquare from "components/CompanyLogoSquare";
import { useAppContextController } from "context/AppContext";

// Function to format the date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Function to format the time
const formatTime = (timeString) => {
  const time = new Date(timeString);
  return time.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Function to add to Google Calendar
const addToGoogleCalendar = (interview) => {
  const {
    eventName,
    companyName,
    jobName,
    address,
    city,
    zip,
    state,
    eventDate,
    eventEndTime,
    description,
  } = interview;

  // Format the address for Google Calendar link
  const location = `${address}, ${city}, ${state} ${zip}`;

  // Format dates for Google Calendar
  const startDate = new Date(eventDate);
  const endDate = new Date(eventEndTime);

  const startDateString = startDate.toISOString().replace(/-|:|\.\d+/g, "");
  const endDateString = endDate.toISOString().replace(/-|:|\.\d+/g, "");

  const calendarTitle = `${eventName} - ${companyName} (${jobName})`;
  const calendarDetails = description;
};

const EmployeeInterviewPreviewModal = ({ open, onClose, interview }) => {
  const { company } = useAppContextController();

  if (!interview) return null;

  const {
    eventName,
    companyName,
    venueName,
    jobName,
    interviewLocation,
    interviewType,
    eventDate,
    eventEndTime,
    jobLogo,
    description,
    recruiter,
  } = interview;

  const { address, city, state, zip } = interviewLocation || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {/* Header with Company Logo */}
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {!!jobLogo && jobLogo !== "No" && (
            <Box
              component="img"
              src={jobLogo}
              alt={`${companyName || venueName} logo`}
              sx={{
                height: 50,
                width: 50,
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          )}
          <Box>
            <Typography variant="h6" component="div">
              {eventName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {companyName || venueName}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Main Interview Details */}
          <Grid container item xs={3} flexDirection="column" justifyContent="space-between">
            <CompanyLogoSquare
              company={{ name: interview?.companyName }}
              defaultCompany={company}
              // height={120}
              width="100%"
              styles={{
                blockSize: "auto",
              }}
            />
            <AddToCalendarButton event={interview} />
          </Grid>
          <Grid
            item
            xs={9}
            sx={{
              mt: 2,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <BusinessIcon color="info" sx={{ mr: 1 }} fontSize="medium" />
                <Typography variant="h6">{jobName}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon color="info" sx={{ mr: 1 }} fontSize="medium" />
                <Typography variant="body1">
                  {typeof recruiter === "string"
                    ? recruiter
                    : `${recruiter?.firstName} ${recruiter.lastName}`}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarTodayIcon color="info" sx={{ mr: 1 }} fontSize="medium" />
                <Typography variant="body1">{formatDate(eventDate)}</Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AccessTimeIcon color="info" sx={{ mr: 1 }} fontSize="medium" />
                <Typography variant="body1">
                  {formatTime(eventDate)} - {formatTime(eventEndTime)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                <LocationOnIcon color="info" sx={{ mr: 1, mt: 0.5 }} fontSize="medium" />
                {interviewType === "Online" && <Typography variant="body1">Online</Typography>}
                {interviewType !== "Online" && !!interviewLocation && (
                  <Typography variant="body1">
                    {address}
                    <br />
                    {city}, {state} {zip}
                  </Typography>
                )}
                {interviewType !== "Online" && !interviewLocation && (
                  <Typography variant="body1">Online</Typography>
                )}
              </Box>
            </Paper>

            {/* Description Section */}
            {description && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <EventNoteIcon color="info" sx={{ mr: 1 }} fontSize="medium" />
                  <Typography variant="h6">Interview Details</Typography>
                </Box>
                <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                  {description}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeInterviewPreviewModal;
